import { useState, useEffect } from 'react'
import { Settings, Type, Sun, Upload, Download } from 'lucide-react'
import toast from 'react-hot-toast'

// ===== Types =====

export interface ChatSettings {
  fontFamily: string
  fontSize: number
  fontWeight: number // 300-900
  opacity: number // 0.3-1.0
}

const DEFAULT_SETTINGS: ChatSettings = {
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 14,
  fontWeight: 500,
  opacity: 0.85,
}

const STORAGE_KEY = 'neuralspace-chat-settings'

// ===== Storage =====

export function loadChatSettings(): ChatSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) }
    }
  } catch {}
  return { ...DEFAULT_SETTINGS }
}

export function saveChatSettings(settings: ChatSettings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
}

export function applyChatSettings(settings: ChatSettings) {
  const root = document.documentElement
  root.style.setProperty('--chat-font-family', settings.fontFamily)
  root.style.setProperty('--chat-font-size', `${settings.fontSize}px`)
  root.style.setProperty('--chat-font-weight', String(settings.fontWeight))
  root.style.setProperty('--chat-text-opacity', String(settings.opacity))
}

// Reset to defaults
export function resetChatSettings() {
  saveChatSettings(DEFAULT_SETTINGS)
  applyChatSettings(DEFAULT_SETTINGS)
}

const FONT_OPTIONS = [
  { label: 'Inter (по умолчанию)', value: 'Inter, system-ui, sans-serif' },
  { label: 'JetBrains Mono', value: '"JetBrains Mono", monospace' },
  { label: 'PT Sans', value: '"PT Sans", sans-serif' },
  { label: 'PT Serif', value: '"PT Serif", serif' },
  { label: 'Roboto', value: 'Roboto, sans-serif' },
  { label: 'Open Sans', value: '"Open Sans", sans-serif' },
  { label: 'Montserrat', value: 'Montserrat, sans-serif' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Times New Roman', value: '"Times New Roman", serif' },
]

// ===== Component =====

interface ChatSettingsPanelProps {
  onImport: () => void
  onClose: () => void
}

export default function ChatSettingsPanel({ onImport, onClose }: ChatSettingsPanelProps) {
  const [settings, setSettings] = useState<ChatSettings>(loadChatSettings)

  const updateSetting = <K extends keyof ChatSettings>(key: K, value: ChatSettings[K]) => {
    const updated = { ...settings, [key]: value }
    setSettings(updated)
    saveChatSettings(updated)
    applyChatSettings(updated)
  }

  return (
    <>
      <div className="fixed inset-0 z-10" onClick={onClose} />
      <div className="absolute bottom-full left-0 mb-2 w-72 glass-card z-20 p-3 space-y-3 max-h-[70vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-gray-300 flex items-center gap-1.5">
            <Settings className="w-3.5 h-3.5" />
            Настройки чата
          </h3>
          <button
            onClick={() => { resetChatSettings(); setSettings(loadChatSettings()) }}
            className="text-[10px] text-gray-500 hover:text-indigo-400 transition-colors"
          >
            Сбросить
          </button>
        </div>

        {/* Font family */}
        <div>
          <label className="text-[10px] text-gray-500 block mb-1 flex items-center gap-1">
            <Type className="w-3 h-3" /> Шрифт
          </label>
          <select
            value={settings.fontFamily}
            onChange={(e) => updateSetting('fontFamily', e.target.value)}
            className="w-full px-2 py-1.5 rounded-lg bg-gray-800 border border-white/10 text-xs text-gray-200 focus:border-indigo-500/50 outline-none"
          >
            {FONT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value} style={{ fontFamily: opt.value }}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Font size */}
        <div>
          <label className="text-[10px] text-gray-500 block mb-1">
            Размер шрифта: {settings.fontSize}px
          </label>
          <input
            type="range"
            min={10}
            max={24}
            step={1}
            value={settings.fontSize}
            onChange={(e) => updateSetting('fontSize', Number(e.target.value))}
            className="w-full accent-indigo-500"
          />
          <div className="flex justify-between text-[9px] text-gray-600 mt-0.5">
            <span>10px</span>
            <span>24px</span>
          </div>
        </div>

        {/* Font weight */}
        <div>
          <label className="text-[10px] text-gray-500 block mb-1">
            Насыщенность: {settings.fontWeight}
          </label>
          <input
            type="range"
            min={300}
            max={900}
            step={100}
            value={settings.fontWeight}
            onChange={(e) => updateSetting('fontWeight', Number(e.target.value))}
            className="w-full accent-indigo-500"
          />
          <div className="flex justify-between text-[9px] text-gray-600 mt-0.5">
            <span>Light</span>
            <span>Bold</span>
          </div>
        </div>

        {/* Opacity / Brightness */}
        <div>
          <label className="text-[10px] text-gray-500 block mb-1 flex items-center gap-1">
            <Sun className="w-3 h-3" /> Яркость текста: {Math.round(settings.opacity * 100)}%
          </label>
          <input
            type="range"
            min={30}
            max={100}
            step={5}
            value={Math.round(settings.opacity * 100)}
            onChange={(e) => updateSetting('opacity', Number(e.target.value) / 100)}
            className="w-full accent-indigo-500"
          />
          <div className="flex justify-between text-[9px] text-gray-600 mt-0.5">
            <span>30%</span>
            <span>100%</span>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/5 pt-2">
          <h4 className="text-[10px] font-semibold text-gray-500 mb-2">Данные</h4>

          {/* Import button */}
          <button
            onClick={() => { onImport(); onClose() }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-gray-400 hover:text-indigo-300 hover:bg-white/5 transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            Импорт истории диалогов
          </button>
        </div>
      </div>
    </>
  )
}
